// AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContextA = createContext();

export const useAuth = () => {
  const context = useContext(AuthContextA);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProviderA');
  }
  return context;
};

// Configure axios defaults
const API_BASE_URL = 'https://chainfree.info:7001';
axios.defaults.baseURL = API_BASE_URL;

export const AuthProviderA = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      // Set axios default header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/user/profile');
      
      if (response.data.success) {
        setUser(response.data.data);
      } else {
        localStorage.removeItem('authToken');
        delete axios.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      localStorage.removeItem('authToken');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const signup = async (formData) => {
    try {
      // Transform frontend data to match your server's expected format
      const serverData = {
        full_name: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        password: formData.password,
        phone_number: formData.phone || null,
        date_of_birth: formData.dateOfBirth || null,
        newsletter_subscribed: formData.newsletter,
        marketing_emails: formData.marketing
      };

      console.log('Sending signup data:', { ...serverData, password: '[HIDDEN]' });

      const response = await axios.post('/auth/signup', serverData);

      if (response.data.success) {
        // Store token and set axios header
        localStorage.setItem('authToken', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Set user data
        setUser(response.data.data);
        
        return { 
          success: true, 
          message: response.data.message, 
          autoLogin: true 
        };
      }
    } catch (error) {
      console.error('Signup error:', error);
      
      if (error.response?.data) {
        // Server returned an error response
        const errorData = error.response.data;
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          // Validation errors from express-validator
          const validationErrors = errorData.errors.map(err => err.msg).join(', ');
          return { success: false, error: validationErrors };
        }
        
        return { 
          success: false, 
          error: errorData.message || 'Signup failed' 
        };
      }
      
      return { 
        success: false, 
        error: 'Network error. Please try again.' 
      };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        // Store token and set axios header
        localStorage.setItem('authToken', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
        
        // Set user data
        setUser(response.data.data);
        
        return { success: true };
      }
    } catch (error) {
      console.error('Login error:', error);
      
      if (error.response?.data) {
        return { 
          success: false, 
          error: error.response.data.message || 'Login failed' 
        };
      }
      
      return { 
        success: false, 
        error: 'Network error. Please try again.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const value = {
    user,
    login,
    signup,
    logout,
    loading
  };

  return (
    <AuthContextA.Provider value={value}>
      {children}
    </AuthContextA.Provider>
  );
};